import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Pencil, Trash2, Phone, Mail, MessageCircle, Package, Loader2 } from 'lucide-react'
import { fetchClientsPage } from '@/lib/clientsApi'
import type { PageProps, Client, Pagination } from '@/types'

interface ClientsIndexProps extends PageProps {
  clients: Client[]
  pagination: Pagination
  q?: string
  can_create?: boolean
  can_edit?: boolean
  can_delete?: boolean
}

export default function ClientsIndex({
  clients: initialClients,
  pagination: initialPagination,
  q: initialQuery = '',
  can_create = true,
  can_edit = true,
  can_delete = true,
  current_user,
  current_organization,
  current_business,
  flash,
}: ClientsIndexProps) {
  const orgSlug = current_organization?.slug || ''
  const [query, setQuery] = useState(initialQuery)
  const [clients, setClients] = useState(initialClients)
  const [pagination, setPagination] = useState(initialPagination)
  const [loadingMore, setLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef(false)
  const skipSearchFetch = useRef(true)

  useEffect(() => {
    setClients(initialClients)
    setPagination(initialPagination)
  }, [initialClients, initialPagination])

  useEffect(() => {
    if (skipSearchFetch.current) {
      skipSearchFetch.current = false
      return
    }
    const handle = window.setTimeout(async () => {
      const page = await fetchClientsPage(orgSlug, { page: 1, q: query })
      setClients(page.clients)
      setPagination(page.pagination)
    }, 300)
    return () => window.clearTimeout(handle)
  }, [query, orgSlug])

  const loadMore = useCallback(async () => {
    if (loadingRef.current || !pagination.has_more) return
    loadingRef.current = true
    setLoadingMore(true)
    try {
      const page = await fetchClientsPage(orgSlug, { page: pagination.page + 1, q: query })
      setClients((current) => {
        const seen = new Set(current.map((client) => client.id))
        return current.concat(page.clients.filter((client) => !seen.has(client.id)))
      })
      setPagination(page.pagination)
    } finally {
      loadingRef.current = false
      setLoadingMore(false)
    }
  }, [orgSlug, pagination.has_more, pagination.page, query])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore()
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [loadMore])

  const handleDelete = (client: Client) => {
    if (confirm(`¿Estás seguro de eliminar a "${client.name}"?`)) {
      router.delete(`/${orgSlug}/clients/${client.id}`)
    }
  }

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Clientes</h1>
            <p className="text-muted-foreground">
              Gestiona tu lista de clientes
            </p>
          </div>
          {can_create && (
            <Link href={`/${orgSlug}/clients/new`} className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                Nuevo cliente
              </Button>
            </Link>
          )}
        </div>

        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Buscar por nombre o teléfono..."
          aria-label="Buscar clientes"
        />

        {flash?.notice && (
          <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
            {flash.notice}
          </div>
        )}

        {flash?.alert && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {flash.alert}
          </div>
        )}

        {clients.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center">
            <p className="text-muted-foreground">
              {query ? 'No se encontraron clientes' : 'No tienes clientes registrados'}
            </p>
            {can_create && !query && (
              <Link href={`/${orgSlug}/clients/new`} className="mt-4 inline-block">
                <Button variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar tu primer cliente
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card">
            <div className="divide-y divide-border">
              {clients.map((client) => (
                <div
                  key={client.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <Link
                    href={`/${orgSlug}/clients/${client.id}`}
                    className="flex min-w-0 items-center gap-4 rounded-lg hover:bg-accent/50"
                  >
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
                      {client.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-card-foreground truncate">
                        {client.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{client.phone}</span>
                        </span>
                        {client.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{client.email}</span>
                          </span>
                        )}
                        {client.whatsapp_opted_in && (
                          <span className="flex items-center gap-1 text-green-500">
                            <MessageCircle className="h-3.5 w-3.5 flex-shrink-0" />
                            WhatsApp
                          </span>
                        )}
                        {(client.active_packages_count || 0) > 0 && (
                          <span className="flex items-center gap-1 text-primary">
                            <Package className="h-3.5 w-3.5" />
                            {client.active_packages_count} paquetes activos ·{" "}
                            {client.package_sessions_available} sesiones
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 pl-14 sm:pl-0">
                    {(can_edit || can_delete) && (
                      <>
                      {can_edit && (
                        <Link href={`/${orgSlug}/clients/${client.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                      {can_delete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(client)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div ref={sentinelRef} className="flex justify-center py-3">
              {loadingMore && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
